import {HTTP} from '../../services/baseHttpSvc';
import _ from 'lodash';
import { convertAnyWho5AssessmentsToPositiveBaseScore } from '@/utilities/convertWho5Scores.js';

require('promise.prototype.finally').shim();

// Any patient info that is returned or used in API calls should be stored here

const initialFilters = {
  // assessment severities
  severities: [],
  // filter by improving or declining trends
  assessmentTrend: null,
  // moodTrend is not used currently
  moodTrend: null,
  // list of access codes
  accessCodes: [],
  // list of wellness programs
  focuses: [],
  // list of referrer names
  referrers: [],
  // Number of days within which an interaction has occured
  lastInteractionDays: null,
  // Values are either 'notReviewed' or 'reviewedWithin'
  reviewType: null,
  // Patient has not been reviewed within last number of days
  lastReviewedDays: 1
};

const state = {
  // list of patients to display in dashboard
  patients: [],
  // the total number of patients in patient list
  patientCount: null,
  // display settings for dashboard
  dashboardSettings: {
    pageSize: 20,
    page: 1
  },
  // list of patients queried on search page
  patientSearch: [],
  // a "pending" indicator
  patientsLoading: false,
  // patient details, keyed by Id
  patientDetails: {
    firstName: '',
    emailAddress: '',
    accessCode: '',
    lastReviewed: '',
    crisisStatus: '',
    assessmentScores: [],
    trackerHistory: '',
    orgShortName: '',
    telehealthProvider: '',
    coachingUser: '',
    isAdolescent: false,
    gender: '',
    otherGender: '',
    sexualOrientation: '',
    otherSexualOrientation: '',
    pronouns: '',
    otherPronouns: ''
  },
  // true if patient detail is not found
  patientNotFound: false,
  // "pending" indicator for patient details
  patientDetailsLoading: false,
  // activity history for patient
  patientActivities: null,
  // user-selected filters on patient data
  patientFilters: Object.assign({}, initialFilters),
  // the id of selected snapshot patient - saved in store so it is persisted on back button
  patientSnapshotId: 0,
  // the help resources that would be displayed to user in "Show Help" screen
  helpResources: null
};

// One school of thought is that getters should not be created for simple retrieval from state.
// However, having even simple properties in getters makes for a consistent retrieval of state from components using mapGetters.
const getters = {
  patients: state => state.patients,
  patientCount: state => state.patientCount,
  dashboardSettings: state => state.dashboardSettings,
  patientSearch: state => state.patientSearch,
  patientsLoading: state => state.patientsLoading,
  patientFilters: state => state.patientFilters,
  hasPatientFilters: state => {
    if (state.patientFilters.severities.length > 0 ||
      state.patientFilters.assessmentTrend ||
      state.patientFilters.moodTrend ||
      state.patientFilters.accessCodes.length > 0 ||
      state.patientFilters.focuses.length > 0 ||
      state.patientFilters.referrers.length > 0 ||
      state.patientFilters.lastInteractionDays ||
      state.patientFilters.reviewType) {
      return true;
    }
    return false;
  },
  patientDetailsLoading: state => state.patientDetailsLoading,
  getPatientDetailsById: (state) => (id) => state.patientDetails[id],
  patientNotFound: state => state.patientNotFound,
  patientActivities: state => state.patientActivities,
  patientSnapshotId: state => state.patientSnapshotId,
  helpResources: state => state.helpResources
};

// Components should call actions rather than mutators directly
const actions = {
  getPatientList(context) {
    const params = getFilterParams(context.getters.patientFilters, context.getters.dashboardSettings);
    context.commit('setPatientsLoading', true);
    return HTTP.post('/patients', params).then(response => {
      const count = response.data.totalCount;
      context.commit('setPatientCount', count);
      context.commit('setPatientsLoading', false);

      if (count === 0) {
        context.commit('setPatients', []);
      } else {
        for (let patient of response.data.patients) {
          convertAnyWho5AssessmentsToPositiveBaseScore(patient.assessmentScores);
        }
        context.commit('setPatients', response.data.patients);
      }

      let patient = null;
      if (count > 0) {
        patient = response.data.patients.find(p => p.id === context.state.patientSnapshotId);
      }
      if (!patient) {
        context.commit('setPatientSnapshotId', 0);
      }
    }).finally(() => context.commit('setPatientsLoading', false));
  },
  searchPatients(context, searchParams) {
    let result = {
      emailAddress: searchParams.email,
      firstName: searchParams.firstName,
      lastName: searchParams.lastName,
      dateOfBirthYear: searchParams.dobYear,
      dateOfBirthMonth: searchParams.dobMonth,
      dateOfBirthDay: searchParams.dobDay
    };
    return HTTP.post('/patients/search/users', result).then(response => {
      context.commit('setPatientSearch', response.data);
    });
  },
  getPatientDetails(context, id) {
    context.commit('setPatientDetailsLoading', true);
    return HTTP.get('/patients/' + id).then(response => {
      let patient = response.data;
      convertAnyWho5AssessmentsToPositiveBaseScore(patient.assessmentScores);

      context.commit('setPatientDetails', patient);
      context.commit('setPatientNotFound', false);
      context.commit('setPatientDetailsLoading', false);
    }).catch((error) => {
      if (error.response && error.response.status === 404) {
        context.commit('setPatientNotFound', true);
      }
      context.commit('setPatientDetailsLoading', false);
    });
  },
  getPatientActivityHistory(context, id) {
    return HTTP.get('/patients/' + id + '/activities').then(response => {
      context.commit('setPatientActivities', response.data);
    });
  },
  markPatientAsReviewed(context, patientId) {
    return HTTP.post('/patients/notes', { 'userId': patientId }).then(response => {
      // refresh patient details
      context.dispatch('getPatientDetails', patientId);
    });
  },
  getPatientHelpResources(context, params) {
    return HTTP.get('/collateral/headers/organizations/' + params.orgName + '/accesscodes/' + params.accessCode).then(response => {
      context.commit('setHelpResources', response.data);
    });
  }
};

const mutations = {
  setPatients(state, patientArray) {
    state.patients = patientArray;
  },
  setPatientCount(state, count) {
    state.patientCount = count;
  },
  setDashboardSettings(state, settings) {
    // merge settings. settings wins if any collisions
    state.dashboardSettings = {...state.dashboardSettings, ...settings};
  },
  setPatientSearch(state, patientArray) {
    state.patientSearch = patientArray;
  },
  setPatientsLoading(state, loading) {
    state.patientsLoading = loading;
  },
  setPatientFilters(state, filters) {
    if (filters && Object.keys(filters).length > 0) {
      // merge filters with patientFilters. filters wins if there are any collisions.
      state.patientFilters = {...state.patientFilters, ...filters};
    } else {
      // reset to initial state
      state.patientFilters = initialFilters;
    }
    state.patientSnapshotId = 0;
  },
  setPatientDetails(state, patient) {
    // vuex doesn't update correctly unless you replace state instead of update
    var details = Object.assign({}, state.patientDetails);
    let result = details[patient.id] ? details[patient.id] : {};
    // combine existing with new
    details[patient.id] = Object.assign(result, patient);
    state.patientDetails = details;
  },
  setPatientActivities(state, activities) {
    state.patientActivities = activities;
  },
  setPatientNotFound(state, found) {
    state.patientNotFound = found;
  },
  setPatientDetailsLoading(state, loading) {
    state.patientDetailsLoading = loading;
  },
  setPatientSnapshotId(state, id) {
    state.patientSnapshotId = id;
  },
  setHelpResources(state, help) {
    state.helpResources = help;
  }
};

// internal helper functions
function getFilterParams(filters, pageSettings) {
  var params = {
    'rowCount': pageSettings.pageSize,
    'page': pageSettings.page,
    'currentFocus': filters.focuses,
    'referrerNames': filters.referrers,
    'accessCodes': filters.accessCodes,
    'lastReviewedDays': filters.lastReviewedWithinDays,
    'assessmentSeverity': _.clone(filters.severities),
    'notReviewed': filters.reviewType === 'notReviewed'
  };
  if (filters.reviewType === 'reviewedWithin') {
    params.lastReviewedDays = filters.lastReviewedDays;
  }
  if (!Number.isNaN(filters.lastInteractionDays)) {
    params.lastInteractionDays = filters.lastInteractionDays;
  }
  if (!_.isEmpty(params.assessmentSeverity) &&
     _.findIndex(params.assessmentSeverity, function(s) { return s === 'SEVERE'; }) > -1) {
    // filtering by severe should also retrieve Extremely Severe
    params.assessmentSeverity.push('EXTREMELYSEVERE');
  }
  if (filters.assessmentTrend) {
    params.assessmentTrendingStatus = filters.assessmentTrend.toUpperCase();
  }
  if (filters.moodTrend) {
    params.moodTrendingStatus = filters.moodTrend.toUpperCase();
  }

  return params;
}

export default {
  state,
  getters,
  actions,
  mutations
};
